import { NEXOYA_CONTACT_EMAIL } from './config';
import { PRODUCT_NAME } from './copy';

export type InterestMailtoInput = {
  /**
   * The tenant's own support address (`customization.supportMail`). Passed in rather than
   * read from the store so the fallback is testable in the node project.
   */
  to?: string | null;
  /** The capability the analyst was looking at — `upsellCopyFor(feature).title`. */
  capability: string;
  /** True on the Nexoya tenant. Decides the register; a white-label mail names no vendor. */
  isNexoyaTenant: boolean;
};

/**
 * Subject prefix on the Nexoya tenant.
 *
 * Read from `copy.ts` rather than written out again: this constant carried "Nexoya Full" for a
 * release after the register had been renamed to Premium, so sales received inbound interest
 * in a product name that no longer existed, from a dialog that had just said something else.
 * Neither test could see it — `copy.test.ts` forbade the old name, this module's test pinned
 * it, and no test imported both modules.
 */
export const INTEREST_SUBJECT_PREFIX_NEXOYA = PRODUCT_NAME;

/** Subject prefix on a white-label tenant. Names no vendor. */
export const INTEREST_SUBJECT_PREFIX_WHITE_LABEL = 'Full platform';

/**
 * Builds the `mailto:` href behind the interest CTA (`INTEREST_CTA_LABEL`).
 *
 * Pure on purpose: the subject and body are product copy, and product copy deserves a test
 * that does not need a DOM. `openMailto` below is the only impure line, and it is a separate
 * export so component tests can mock the seam instead of stubbing `window.location`.
 *
 * Both parameters are percent-encoded rather than concatenated raw. The capability is
 * `copy.ts`'s title verbatim, edited by people who are not thinking about URI grammar: a
 * single `&` in it ("Budget & pacing") would otherwise close the `subject` parameter and
 * turn the rest of the sentence into a third parameter the mail client silently drops.
 *
 * The register is `copy.ts`'s. The reader is an analyst who cannot buy, so the body asks a
 * question rather than placing an order, and it promises nothing on the product's behalf —
 * nothing here sends a mail, confirms anything or schedules a callback. The user's own
 * client sends whatever they choose to send, after they have read it.
 */
export function buildInterestMailto(input: InterestMailtoInput): string {
  // Validated, not encoded: `encodeURIComponent` escapes `@` and some clients reject that. A `?`
  // in the tenant's configured address would open the header list (`?bcc=…`) and a stray space
  // yields an href every client silently drops, so anything unusable falls back.
  const trimmed = typeof input.to === 'string' ? input.to.trim() : '';
  const wellFormed = /^[^\s?&#,;:<>"]+@[^\s?&#,;:<>"]+$/.test(trimmed) ? trimmed : null;
  /**
   * D6, in the one field the register sweep cannot see — it reads copy, never the recipient.
   *
   * Two ways our address reaches a white-label tenant, and BOTH have to be closed here: the
   * tenant configured none (so `to` is empty), or `queryTeam.ts` filled it in from
   * `DEFAULT_UI_CUSTOMIZATION_FALLBACK`, whose `supportMail` is this module's own
   * `NEXOYA_CONTACT_EMAIL`. The second is the one that shipped: the address is perfectly
   * well-formed, so an "is it usable" check waves it straight through, and an agency's client
   * ends up with a mail addressed to their vendor's vendor.
   *
   * `mailto:?subject=…` is valid — the client opens with an empty To for the user to fill,
   * which is the honest outcome when we have no address we are allowed to name.
   */
  const isVendorAddress = /@nexoya\.(io|com)$/i.test(trimmed);
  const usable = wellFormed && !(isVendorAddress && !input.isNexoyaTenant) ? wellFormed : null;
  const address = usable ?? (input.isNexoyaTenant ? NEXOYA_CONTACT_EMAIL : '');

  const prefix = input.isNexoyaTenant ? INTEREST_SUBJECT_PREFIX_NEXOYA : INTEREST_SUBJECT_PREFIX_WHITE_LABEL;

  const subject = `${prefix} — ${input.capability}`;

  const body = input.isNexoyaTenant
    ? `Hi,\n\nI'd like to know more about the full Nexoya platform, in particular:\n${input.capability}\n\nSent from the Nexoya app.`
    : `Hi,\n\nI'd like to know more about the full platform, in particular:\n${input.capability}\n\nSent from the app.`;

  return `mailto:${address}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

/**
 * The navigation seam, called by name so `UpsellDialog`'s jsdom test can `vi.mock` it — jsdom has
 * no navigation and assigning `window.location.href` logs "Not implemented". Mock the seam, not
 * the browser: `useIntegrationPopup` in `src/hooks/useIntegrationPopup.component.test.tsx`.
 */
export function openMailto(href: string): void {
  window.location.href = href;
}
